import React, {useEffect, useState, ChangeEvent} from 'react';
import Modal from 'shared/components/modal';
import AssetService from 'src/services/AssetService';
import Label from 'shared/components/label';
import {isEmpty} from 'lodash';
import {Formik, Form, FormikValues} from 'formik';
import FormikDropdown from 'src/components/formik-dropdown';
import FormikTextInput from 'src/components/inputs/formik-text-input';
import Dropdown, {DropdownOption} from 'src/components/dropdown';

interface RequestHighSchool {
  country: string;
  subdivision: string;
  city: string;
  schoolName: string;
}

interface Props {
  countries: {
    value: string;
    text: string;
  }[];

  hideModal: () => void;
  handleSubmit: (values: FormikValues) => void;
  onSwitchCountry: (country: string) => void;
  requestHighSchool: RequestHighSchool;
}

const RequestHighSchoolModal = ({
  hideModal,
  countries,
  handleSubmit,
  requestHighSchool,
  onSwitchCountry,
}: Props) => {
  const [divisionOptions, setDivisionOptions] = useState<DropdownOption[]>([]);
  const [subdivisionsLoading, setSubdivisionsLoading] = useState<boolean>(true);

  useEffect(() => {
    refreshSubdivisions(requestHighSchool.country);
  }, [requestHighSchool.country]);

  const refreshSubdivisions = async (country: string) => {
    setSubdivisionsLoading(true);
    const subdivisions = await AssetService.getSubdivisionOptions(country);

    const dropdownOptions: DropdownOption[] = subdivisions.map((subdivision) => ({
      value: String(subdivision.value),
      text: subdivision.text,
    }));

    setDivisionOptions(dropdownOptions);
    setSubdivisionsLoading(false);
  };

  const onCountrySelect = (
    e: ChangeEvent<HTMLSelectElement>,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const selectedCountry = e.target.value;
    setFieldValue('country', selectedCountry);
    setFieldValue('subdivision', '');
    onSwitchCountry(selectedCountry);
    refreshSubdivisions(selectedCountry);
  };

  const validate = (values: RequestHighSchool) => {
    const errors: Record<string, string> = {};

    ['country', 'subdivision', 'city', 'schoolName'].forEach((field) => {
      if (isEmpty(values[field])) {
        errors[field] = 'Field is required';
      }
    });

    return errors;
  };

  return (
    <Formik
      initialValues={requestHighSchool}
      validate={validate}
      onSubmit={(values, {setSubmitting}) => {
        setSubmitting(false);
        handleSubmit(values);
      }}
    >
      {({isSubmitting, setFieldValue}) => (
        <>
          <Modal.TextHeader
            onClose={hideModal}
            textAlign="center"
            subtitle="Most schools in the U.S. are eligible for Raise Me, but not all of them are on our list. Tell us more, and we’ll look into adding your school within 1 business day."
          >
            Send a Request
          </Modal.TextHeader>
          <Form>
            <Modal.Body>
              <legend className="shared-required-legend"></legend>

              <Label>
                Country of your High School
                <Dropdown
                  name="country"
                  options={countries}
                  onChange={(e) => onCountrySelect(e, setFieldValue)}
                  value={requestHighSchool.country}
                  required
                />
              </Label>

              {divisionOptions.length > 0 && (
                <Label required>
                  State of your high school
                  <FormikDropdown
                    name="subdivision"
                    options={divisionOptions}
                    disabled={subdivisionsLoading}
                    required
                  />
                </Label>
              )}

              <Label required>
                City of your high school
                <FormikTextInput
                  required
                  showCheck
                  placeholder="e.g. San Francisco"
                  aria-label="City"
                  name="city"
                />
              </Label>

              <Label required>
                Name of your high school
                <FormikTextInput
                  required
                  showCheck
                  placeholder="e.g. Martin Luther King High School"
                  aria-label="School Name"
                  name="schoolName"
                />
              </Label>
            </Modal.Body>
            <Modal.Footer
              primaryButton={{
                text: 'Send',
                type: 'submit',
                width: '100%',
                disabled: isSubmitting,
              }}
            />
          </Form>
        </>
      )}
    </Formik>
  );
};

export default RequestHighSchoolModal;
